import api from '@/plugins/api'
import CustomEvent from '@/plugins/custom-event'
import { spinnerGrow } from '@/htmlBuild/loading'

/**
 * @typedef {("appendedItems", "lastPage")} InfinityScroll.EventsName
 */

/**
 * @typedef InfinityScroll.InitEvents
 * @property {function(params: any):void} [appendedItems]
 * @property {function(params: any):void} [lastPage]
 */

/**
 * @typedef InfinityScroll.Api
 * @property {string} url
 * @property {Object} [data]
 * @property {number} [pageNumber]
 * @property {number} [pageSize]
 * @property {function(): Promise<InfinityScroll.Response>} [ajax]
 */

/**
 * @typedef InfinityScroll.Response
 * @property {array} data,
 * @property {number} currentPage
 * @property {number} lastPage
 * @property {number} total
 */

/**
 * @typedef InfinityScroll.Options
 * @property {function(item: Object): any} [appendItem]
 * @property {InfinityScroll.Api} [api]
 * @property {function} [loadingElement]
 * @property {function} [noNotificationElement]
 * @property {InfinityScroll.InitEvents} [events]
 * @property {number} [offsetScrollHeight]
 * @property {boolean} [orientationReverse]
 * @property {string} [fieldUniqueControl]
 * @property {boolean} [manualLoad]
 */

class ElementNotFoundException {
    constructor(element) {
        this.message = "Não foi possível localizar o elemento: " + element
        this.name = "ElementNotFound"
    }
}

function defaultOptions() {
    return {
        appendItem() {},

        loadingElement() {
            let el = document.createElement('div')
            el.className = 'bg-white text-center p-3'

            el.append(spinnerGrow())

            return el
        },

        noNotificationElement() {},

        offsetScrollHeight: 50,

        orientationReverse: false,

        fieldUniqueControl: 'id',

        manualLoad: false,
    }
}

function defaultApi() {
    return {
        url: '',

        data: {},

        pageNumber: 0,

        pageSize: 10,

        /**
         * @this InfinityScroll
         * @returns {Promise<InfinityScroll.Response>}
         */
        ajax() {
            return api(this.api.url, {
                data: this.prepareDataApi()
            }).then(data => {
                return {
                    data: data.data || [],
                    currentPage: data.meta?.current_page || 1,
                    lastPage: data.meta?.last_page || 1,
                    total: data.meta?.total || 0,
                }
            })
        },
    }
}

export default class InfinityScroll {
    /** @type InfinityScroll.Options */
    #options = defaultOptions()

    /** @type InfinityScroll.Api */
    api = defaultApi()

    /** @type CustomEvent */
    #event

    #runOnceLoadedQueue = []

    #isDestroyed = false

    /**
     * @param {Element | string} element
     * @param {InfinityScroll.Options} options
     */
    constructor(element, options = {}) {
        this.element = element instanceof Element ? element : document.querySelector(element)

        if (!this.element) {
           throw new ElementNotFoundException(element)
        }

        this.#event = new CustomEvent(this, options.events)

        Object.assign(this.api, options.api || {})

        this.#setOptions(options)

        this.resetStatus()

        this.scrollEvent = () => {
            if (this._loading) {
                return
            }

            let dontLoad = this.#options.orientationReverse ?
                this.element.scrollTop > this.#options.offsetScrollHeight :
                this.element.clientHeight + this.element.scrollTop < this.element.scrollHeight - this.#options.offsetScrollHeight

            if (dontLoad || this.api.pageNumber === this.lastPage) {
                return
            }

            this.load().then(() => {
                // Após o retorno do AJAX é chamado o evento novamente para verificar a posição do scroll pois
                // ao incluir os itens no DOM o usuário pode acabar ativando o scroll muito rapido
                // e algum evento pode ser perdido
                if (!this.#isDestroyed) {
                    this.scrollEvent()
                }
            })
        }

        // Bind Event Scroll
        this.element.addEventListener('scroll', this.scrollEvent)

        !this.#options.manualLoad && this.loadUntilScroll()
    }

    #setOptions(options) {
        Object.assign(this.#options, options)

        this.#options.appendItem = this.#options.appendItem.bind(this)

        this.#options.noNotificationElement = this.#options.noNotificationElement.bind(this)

        if (this.#options.loadingElement) {
            this.loadingElement = typeof this.#options.loadingElement === 'function' ? this.#options.loadingElement() : this.#options.loadingElement

            delete this.#options.loadingElement
        }
    }

    getOptions(key) {
        return this.#options[key]
    }

    resetStatus() {
        this.clearAPIControl()

        this._loading = false

        this.#isDestroyed = false

        this.#runOnceLoadedQueue = []

        this._lastAjaxStatus = {
            error: null
        }
    }

    clearAPIControl() {
        this.api.pageNumber = 0

        this.total = 0

        this.lastPage = null

        this.itemIds = []
    }

    prepareDataApi() {
        return {
            page: {
                number: this.api.pageNumber,
                size: this.api.pageSize
            },
            ...this.api.data
        }
    }

    updateDataApi(data) {
        Object.assign(this.api.data, data)
    }

    reload() {
        this.runOnceLoaded(() => {
            this.resetStatus()

            this.element.innerHTML = ''

            this.loadUntilScroll()
        })
    }

    runOnceLoaded(callback) {
        if (this._loading) {
            this.#runOnceLoadedQueue.push(callback)
        } else {
            callback(this._lastAjaxStatus)
        }
    }

    #executeOnceLoadedQueue() {
        while (this.#runOnceLoadedQueue.length > 0) {
            this.#runOnceLoadedQueue.shift()(this._lastAjaxStatus)

            if (this._loading) {
                break
            }
        }
    }

    async loadUntilScroll(noFirst = false) {
        let first = true
        while (this.element.scrollHeight === this.element.clientHeight) {
            if (
                (this.lastPage !== null && this.api.pageNumber >= this.lastPage) ||
                this._loading ||
                // para não ficar em loop caso o container esteja hidden, portanto não sabendo quando tem scroll
                (this.element.scrollHeight === 0 && this.element.clientHeight === 0 && (!first || noFirst))
            ) {
                break
            }

            await this.load()

            first = false

            // Verifica se o objeto foi destruído enquanto estava sendo feito a chamada via AJAX,
            // para que o loop não seja executado com o objeto destruído
            if (this.#isDestroyed) return
        }
    }

    async load() {
        this.startLoading()

        this.api.pageNumber += 1

        try {
            let response = await this.api.ajax.call(this)

            this.resolveResponse(response)
        } catch (error) {
            this.stopLoading()

            this._lastAjaxStatus.error = error

            this.#executeOnceLoadedQueue()

            throw error
        }
    }

    /**
     * @param {InfinityScroll.Response} response
     */
    resolveResponse(response) {
        // Verifica se o objeto foi destruído enquanto estava sendo feito a chamada via AJAX,
        // para que o loop não seja executado com o objeto destruído
        if (this.#isDestroyed) return

        response.data.forEach(item => this.addItem(item, this.#options.orientationReverse))

        this.api.pageNumber = response.currentPage

        this.lastPage = response.lastPage

        this.total = response.total

        this.trigger('appendedItems')

        this.stopLoading()

        if (this.api.pageNumber >= this.lastPage) {
            if (this.itemIds.length === 0) {
                let noNotificationEl = typeof this.#options.noNotificationElement === 'function' ? this.#options.noNotificationElement() : this.#options.noNotificationElement

                if (noNotificationEl) {
                    this.element.append(noNotificationEl)
                }
            }

            this.trigger('lastPage')
        }

        this._lastAjaxStatus.error = null

        this.#executeOnceLoadedQueue()
    }

    addItem(item, reverse) {
        let id = this.#options.fieldUniqueControl ? item[this.#options.fieldUniqueControl] : null

        if (id && this.itemIds.includes(id)) {
            return
        }

        let el = this.addElement(this.#options.appendItem(item), reverse)

        id && this.itemIds.push(id)

        return el
    }

    addElement(el, reverse) {
        if (!el) return el

        if (this.loadingElement.parentNode) {
            reverse ? this.element.insertBefore(el, this.loadingElement.nextSibling) : this.element.insertBefore(el, this.loadingElement)
        } else {
            reverse ? this.element.prepend(el) : this.element.append(el)
        }

        return el
    }

    isLoading() {
        return this._loading
    }

    startLoading() {
        this._loading = true

        this.element.classList.add('infinity-scroll__is_loading')

        if (this.loadingElement) {
            if (this.#options.orientationReverse) {
                this.element.prepend(this.loadingElement)
            } else {
                this.element.append(this.loadingElement)
            }
        }
    }

    stopLoading() {
        this.loadingElement && this.loadingElement.remove()

        this.element.classList.remove('infinity-scroll__is_loading')

        this._loading = false
    }

    destroy() {
        if (!this.#isDestroyed) {
            this.element.removeEventListener('scroll', this.scrollEvent)

            this.element.innerHTML = ''

            delete this.element

            this.#isDestroyed = true
        }
    }

    /**
     * @param {InfinityScroll.EventsName} eventName
     * @param {function} cb
     */
    on(eventName, cb) {
        this.#event.on(eventName, cb)
    }

    /**
     * @param {InfinityScroll.EventsName} eventName
     * @param {Object} params
     */
    trigger(eventName, params = {}) {
        this.#event.trigger(eventName, params)
    }
}
